import { useMemo, Fragment } from 'react';
import cn from 'classnames';
import { MdOutlineDoneAll } from 'react-icons/md';
import { Button, useDisclosure } from '@ds';
import { usePhoneSupportDayTotals } from '@lp/features/modals/PhoneSupportModal/hooks';
import { PhoneSupportModal } from '@lp/features/modals/PhoneSupportModal/PhoneSupportModal';
import { ScheduleViolationTooltip } from '@lp/features/Scheduler/components';
import { SupportViolationItem } from '@lp/features/Scheduler/types';
import {
  getCSWindowDateRangeLabel,
  getCSWindowLabel,
} from '@lp/features/Scheduler/SchedulerClinicalSpecialists/period.utils';
import { OrganTypeWithoutKidney } from '@/api/apis/case';
import { getKeys, ORGAN_ICON } from '@/utils/helpers';
import { CSResourcesWithShifts } from '@/api/apis/resources/schedule';

type PhoneSupportInfoProps = {
  selectedDate: Date;
  shiftsData: CSResourcesWithShifts;
  daysInterval: Date[];
  startDay: Date;
  showViolations?: boolean;
  violationList?: SupportViolationItem[];
};

export const PhoneSupportInfo = ({
  selectedDate,
  shiftsData,
  daysInterval,
  startDay,
  showViolations,
  violationList,
}: PhoneSupportInfoProps) => {
  const { isOpen: isPhoneSupportModalOpen, onOpen, onClose } = useDisclosure();
  const totals = usePhoneSupportDayTotals([selectedDate], selectedDate, shiftsData);
  const periodLabel = useMemo(() => getCSWindowLabel(selectedDate), [selectedDate]);
  const periodDateRange = useMemo(() => getCSWindowDateRangeLabel(selectedDate), [selectedDate]);
  const isError = showViolations && !!violationList?.length;

  const renderContent = useMemo(() => {
    if (showViolations) {
      return violationList?.length ? (
        <ScheduleViolationTooltip items={violationList} withLabel>
          <div className="px-1.5 h-4 rounded-lg bg-danger-30 text-white text-s-sb">
            {violationList.flatMap((v) => v.messages).length}
          </div>
        </ScheduleViolationTooltip>
      ) : (
        <MdOutlineDoneAll className="text-success-30 min-w-6 min-h-6" />
      );
    }

    return (
      <div className="flex items-center gap-2">
        {/* TMDXLP-14091: Kidneys hidden from Clinical Lead totals. Revert OrganTypeWithoutKidney -> OrganType to restore. */}
        {getKeys(OrganTypeWithoutKidney).map((organ) => {
          const OrganIcon = ORGAN_ICON[organ] ?? Fragment;
          return (
            <div key={organ} className="flex items-center gap-1">
              <OrganIcon key={organ} className="text-base-5 size-5" />
              <span className="text-sm text-base-20">{totals[0][organ] || 0}</span>
            </div>
          );
        })}
      </div>
    );
  }, [showViolations, totals, violationList]);

  return (
    <>
      <Button
        size="md"
        variant="light"
        disableRipple
        onClick={onOpen}
        className={cn('rounded-lg', { 'bg-danger-80 text-danger-5': isError })}
      >
        <span className="text-m-r">Clinical Lead:</span>
        {renderContent}
      </Button>
      {isPhoneSupportModalOpen && (
        <PhoneSupportModal
          daysInterval={daysInterval}
          shiftsData={shiftsData}
          startDay={startDay}
          selectedDate={selectedDate}
          onClose={onClose}
          isOpen={isPhoneSupportModalOpen}
          periodLabel={periodLabel}
          periodDateRange={periodDateRange}
        />
      )}
    </>
  );
};
